import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Icon, IconButton, InputAdornment, InputLabel, Tooltip } from "@mui/material";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";

// "YYYY-MM-DD" (ou com hora) -> data local (new Date("YYYY-MM-DD") seria meia-noite UTC = dia anterior no Brasil)
const paraData = (iso) => {
    if (!iso) return null;
    if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso;
    if (typeof iso === "number") return new Date(iso); // data que veio do backend como número (epoch)
    const [ano, mes, dia] = String(iso).slice(0, 10).split("-").map(Number);
    return ano && mes && dia ? new Date(ano, mes - 1, dia) : null;
};

// Data local -> "YYYY-MM-DD" (sem toISOString, que vira UTC e grava o dia anterior)
const paraIso = (data) => (data
    ? `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`
    : "");

const dataBr = (data) => `${String(data.getDate()).padStart(2, "0")}/${String(data.getMonth() + 1).padStart(2, "0")}/${data.getFullYear()}`;

/**
 * Período padrão das telas: De e Até num campo só, escolhidos no mesmo calendário (flatpickr em modo intervalo, com o
 * tema do sistema). Mostra "05/10/2026 até 09/10/2026" e devolve { inicio, fim } em "YYYY-MM-DD".
 * Escolhido só um dia e fechado o calendário: o período é esse dia; com {@code fimAberto} (ex.: vigência), é "a partir
 * de" esse dia, sem data final ({ inicio, fim: "" }). Limpar devolve os dois vazios.
 */
function MDPeriodo({ label, inicio, fim, onChange, placeholder, disabled, podeLimpar, fimAberto, minDate, maxDate }) {
    const calendario = useRef(null);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    // No escuro, os ícones (limpar e calendário) na cor dos textos de apoio; o MUI deixava em preto 54%
    const corIcone = darkMode ? { color: `${ESCURO.textoSecundario} !important` } : undefined;
    const dataInicio = useMemo(() => paraData(inicio), [inicio]);
    const dataFim = useMemo(() => paraData(fim), [fim]);
    // Mesma referência enquanto as datas não mudam: o react-flatpickr reaplica o valor a cada referência nova, e uma
    // renderização da tela no meio da escolha (ex.: o sino atualizando) descartava o 1º clique
    const valor = useMemo(() => [dataInicio, dataFim].filter(Boolean), [dataInicio, dataFim]);
    const temValor = valor.length > 0;
    const textoAPartirDe = fimAberto && dataInicio && !dataFim ? `A partir de ${dataBr(dataInicio)}` : null;

    // O react-flatpickr (3.10) não reaplica uma lista vazia nem uma que é o começo da anterior (compara com every):
    // "limpar" e "de início e fim para só início" ficavam com a data antiga no campo. Sincroniza aqui, fora da escolha.
    useEffect(() => {
        const instancia = calendario.current;
        if (!instancia || instancia.isOpen) return;
        const noCalendario = instancia.selectedDates.map(paraIso).join("|");
        const desejado = valor.map(paraIso).join("|");
        if (noCalendario !== desejado) instancia.setDate(valor, false);
    }, [valor]);

    // Só o início, com fim em aberto: o flatpickr mostraria só a data; o campo diz "A partir de ..."
    useEffect(() => {
        const instancia = calendario.current;
        if (instancia?.input && textoAPartirDe && !instancia.isOpen) {
            instancia.input.value = textoAPartirDe;
        }
    });

    return (
        <MDBox>
            {label && <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{label}</InputLabel>}
            <Flatpickr
                value={valor.length ? valor : ""}
                options={{
                    mode: "range",
                    locale: Portuguese,
                    dateFormat: "d/m/Y",
                    disableMobile: true, // o calendário nativo do celular não escolhe intervalo
                    minDate,
                    maxDate,
                }}
                onReady={(_datas, _texto, instancia) => { calendario.current = instancia; }}
                onChange={(datas) => {
                    if (datas.length === 2) onChange({ inicio: paraIso(datas[0]), fim: paraIso(datas[1]) });
                    else if (datas.length === 0) onChange({ inicio: "", fim: "" });
                }}
                onClose={(datas) => {
                    if (datas.length === 1) onChange({ inicio: paraIso(datas[0]), fim: fimAberto ? "" : paraIso(datas[0]) });
                }}
                render={({ defaultValue }, ref) => (
                    <MDInput
                        size="small"
                        fullWidth
                        disabled={disabled}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        inputRef={ref}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {podeLimpar && temValor && !disabled && (
                                        <Tooltip title="Limpar período">
                                            <IconButton size="small" onClick={() => onChange({ inicio: "", fim: "" })} sx={{ p: 0.25 }}>
                                                <Icon fontSize="small" sx={corIcone}>close</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <IconButton size="small" disabled={disabled} onClick={() => calendario.current?.open()} sx={{ p: 0.25 }}>
                                        <Icon fontSize="small" sx={corIcone}>calendar_month</Icon>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />
        </MDBox>
    );
}

MDPeriodo.defaultProps = {
    label: "Período",
    inicio: "",
    fim: "",
    placeholder: "Selecione o período",
    disabled: false,
    podeLimpar: true,
    fimAberto: false,
    minDate: undefined,
    maxDate: undefined,
};

MDPeriodo.propTypes = {
    label: PropTypes.string,
    inicio: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    fim: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    podeLimpar: PropTypes.bool,
    fimAberto: PropTypes.bool,
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default MDPeriodo;
